<div class="add-medical-disk-container">
  <mat-toolbar color="primary">
    <mat-icon>album</mat-icon>
    <span style="margin-left: 8px;">Upload Medical Imaging Disk</span>
  </mat-toolbar>

  <div class="content-area">

    <!-- BANDWIDTH BANNER -->
    <div class="bandwidth-banner"
      [class.bw-checking]="bandwidthStatus === 'checking'"
      [class.bw-ok]="bandwidthStatus === 'ok'"
      [class.bw-warn]="bandwidthStatus === 'warn'"
      [class.bw-slow]="bandwidthStatus === 'slow'">
      <mat-icon class="bw-icon">
        {{ bandwidthStatus === 'checking' ? 'hourglass_empty' :
           bandwidthStatus === 'ok'       ? 'signal_wifi_4_bar' :
           bandwidthStatus === 'warn'     ? 'signal_wifi_2_bar' : 'signal_wifi_off' }}
      </mat-icon>
      <span *ngIf="bandwidthStatus === 'checking'">Checking connection speed&hellip;</span>
      <span *ngIf="bandwidthStatus === 'ok'">
        Connection: ~{{ bandwidthMbps }} Mbps &mdash; Ready for upload.
      </span>
      <span *ngIf="bandwidthStatus === 'warn'">
        Connection: ~{{ bandwidthMbps }} Mbps &mdash; Upload may be slow. Keep this tab open throughout.
      </span>
      <span *ngIf="bandwidthStatus === 'slow'">
        Connection appears too slow (&lt;0.5 Mbps). We recommend using a wired or faster Wi-Fi connection before uploading.
      </span>
    </div>

    <!-- CASE REFERENCE SECTION (always visible while idle/ready) -->
    <div *ngIf="(uploadStatus$ | async) === 'idle' || (uploadStatus$ | async) === 'ready'"
         class="case-section mat-elevation-z1">
      <h3 class="case-section-title">
        <mat-icon>folder_special</mat-icon>
        Case Reference
      </h3>

      <mat-radio-group [(ngModel)]="caseMode" (change)="onCaseModeChange()" class="case-mode-group">
        <mat-radio-button value="new">Create new case</mat-radio-button>
        <mat-radio-button value="existing" [disabled]="existingCases.length === 0">
          Add to existing case
          <span *ngIf="existingCases.length === 0" class="hint-text">&nbsp;(none yet)</span>
        </mat-radio-button>
      </mat-radio-group>

      <mat-form-field *ngIf="caseMode === 'new'" appearance="outline" class="case-field">
        <mat-label>Case label</mat-label>
        <input matInput [(ngModel)]="newCaseLabel"
               placeholder="e.g. Smith v Hospital — 2024"
               [disabled]="(uploadStatus$ | async) === 'ready'" />
        <mat-hint>Your internal reference. Can be any label your firm uses.</mat-hint>
      </mat-form-field>

      <mat-form-field *ngIf="caseMode === 'existing'" appearance="outline" class="case-field">
        <mat-label>Select case</mat-label>
        <mat-select [(ngModel)]="selectedCaseId"
                    [disabled]="(uploadStatus$ | async) === 'ready'">
          <mat-option *ngFor="let c of existingCases" [value]="c.id">
            {{ c.label }}
            <span class="case-status-hint">&nbsp;&mdash; {{ c.status }}</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <!-- IDLE: Drop zone -->
    <div *ngIf="(uploadStatus$ | async) === 'idle'">
      <input type="file" #folderInput style="display:none"
             webkitdirectory multiple
             (change)="onFolderSelected($event)" />
      <div class="drop-zone"
           [class.drop-zone-inactive]="!dropZoneActive"
           [class.drop-zone-clickable]="dropZoneActive"
           (click)="onDropZoneClick()"
           (dragenter)="onDragEnter($event)"
           (dragover)="onDragOver($event)"
           (drop)="onDrop($event)">
        <mat-icon class="drop-icon">folder_open</mat-icon>
        <h2>Drop DICOM Disk Folder Here</h2>
        <p *ngIf="!canDropFolder" class="warn-text">Folder drop is not supported by this browser.</p>
        <p *ngIf="canDropFolder && !dropZoneActive" class="hint-text">
          Set a case reference above to enable the drop zone.
        </p>
        <p *ngIf="canDropFolder && dropZoneActive" class="hint-text">
          Drag and drop the folder, or <strong>click here to browse</strong>. Only imaging files listed in the disk index will be uploaded.
        </p>
      </div>
    </div>

    <!-- SCANNING: spinner -->
    <div *ngIf="(uploadStatus$ | async) === 'scanning'" class="status-card">
      <mat-spinner diameter="48"></mat-spinner>
      <p>Scanning files and reading DICOMDIR&hellip;</p>
    </div>

    <!-- READY: preview + start -->
    <div *ngIf="(uploadStatus$ | async) === 'ready'" class="ready-panel">

      <div class="ready-summary">
        <mat-icon color="primary">check_circle</mat-icon>
        <div>
          <strong>{{ totalFileCount }}</strong> imaging file(s) identified from disk index.
          <span *ngIf="estimatedMinutes !== null" class="hint-text">
            &nbsp;Estimated upload time: ~{{ estimatedMinutes }} min.
          </span>
        </div>
      </div>

      <h3 *ngIf="studies.length > 0">Studies ({{ studies.length }})</h3>
      <mat-list *ngIf="studies.length > 0" class="studies-list" dense>
        <mat-list-item *ngFor="let study of studies">
          <mat-icon mat-list-icon>medical_services</mat-icon>
          <span mat-line>{{ study.StudyDescription || 'Unknown Study' }}</span>
          <span mat-line class="study-meta">
            {{ study.StudyDate }}
            <span *ngIf="study.StudyInstanceUID">&bull; {{ study.StudyInstanceUID }}</span>
          </span>
        </mat-list-item>
      </mat-list>

      <p *ngIf="studies.length === 0" class="warn-text">
        No studies parsed from DICOMDIR (non-standard format). Files will still be uploaded.
      </p>

      <div class="action-buttons">
        <button mat-raised-button color="primary"
                (click)="startUpload()"
                [disabled]="bandwidthStatus === 'slow'">
          <mat-icon>cloud_upload</mat-icon>
          Start Upload
        </button>
        <button mat-button (click)="reset()">Cancel</button>
      </div>
      <p *ngIf="bandwidthStatus === 'slow'" class="warn-text hint-text">
        Upload is disabled due to poor connection speed.
      </p>
    </div>

    <!-- UPLOADING: progress -->
    <div *ngIf="(uploadStatus$ | async) === 'uploading'" class="uploading-panel">
      <h3>Uploading {{ fileProgresses.length }} files&hellip;</h3>

      <mat-progress-bar
        mode="determinate"
        [value]="(overallProgress$ | async) ?? 0"
        class="overall-bar">
      </mat-progress-bar>
      <p class="progress-label">
        {{ (overallProgress$ | async) ?? 0 }}% &mdash; {{ doneCount }} / {{ fileProgresses.length }} done
        <span *ngIf="failedCount > 0" class="warn-text">&nbsp;({{ failedCount }} failed)</span>
      </p>

      <mat-list class="file-progress-list" dense>
        <mat-list-item *ngFor="let fp of fileProgresses">
          <mat-icon mat-list-icon
            [class.icon-done]="fp.status === 'done'"
            [class.icon-failed]="fp.status === 'failed'"
            [class.icon-pending]="fp.status === 'pending'"
            [class.icon-uploading]="fp.status === 'uploading'">
            {{ fp.status === 'done' ? 'check_circle' :
               fp.status === 'failed' ? 'error' :
               fp.status === 'uploading' ? 'cloud_upload' : 'hourglass_empty' }}
          </mat-icon>
          <span mat-line class="file-path">{{ fp.relativePath }}</span>
          <span mat-line *ngIf="fp.status === 'failed' && fp.errorMessage" class="error-detail">{{ fp.errorMessage }}</span>
          <mat-progress-bar
            *ngIf="fp.status === 'uploading'"
            mode="determinate"
            [value]="fp.progress"
            mat-line
            class="file-bar">
          </mat-progress-bar>
        </mat-list-item>
      </mat-list>
    </div>

    <!-- DONE -->
    <div *ngIf="(uploadStatus$ | async) === 'done'" class="done-panel">
      <mat-icon class="done-icon">cloud_done</mat-icon>
      <h3>Upload Complete</h3>
      <p>{{ doneCount }} file(s) uploaded successfully for case <strong>{{ currentCaseLabel }}</strong>.</p>
      <div *ngIf="failedCount > 0" class="warn-text">
        <p>{{ failedCount }} file(s) failed to upload.</p>
        <ul class="failed-file-list">
          <ng-container *ngFor="let fp of fileProgresses">
            <li *ngIf="fp.status === 'failed'">
              <span class="file-path">{{ fp.relativePath }}</span>
              <span *ngIf="fp.errorMessage" class="error-detail">&nbsp;— {{ fp.errorMessage }}</span>
            </li>
          </ng-container>
        </ul>
      </div>
      <p class="hint-text">Go to <strong>Imaging Cases</strong> to submit this disk for processing.</p>
      <div class="action-buttons">
        <button mat-raised-button color="primary" (click)="reset()">Upload Another Disk</button>
      </div>
    </div>

    <!-- ERROR -->
    <div *ngIf="(uploadStatus$ | async) === 'error'" class="error-panel">
      <mat-icon class="error-icon">error_outline</mat-icon>
      <h3>An error occurred while scanning files.</h3>
      <button mat-raised-button (click)="reset()">Try Again</button>
    </div>

  </div>
</div>
